<template>
  <app-layout>
    <div class="sc-container">
      <div class="page-wrap">

        <!-- Sidebar -->
        <aside class="page-sidebar">
          <div class="sidebar-card">
            <div class="sidebar-profile">
              <img
                v-if="user.avatar"
                class="sidebar-avatar"
                :src="$avatarSrc(user.avatar, 'users')"
                alt="User avatar"
              />
              <img
                v-else
                class="sidebar-avatar"
                src="/assets/avatar.png"
                alt="User avatar"
              />
              <div class="sidebar-name">{{ user.fname }} {{ user.lname }}</div>
              <div class="sidebar-sub" v-if="user.currentcompany">
                {{ user.current_position }} &mdash; {{ user.currentcompany.name }}
              </div>
              <div class="sidebar-sub" v-else>{{ user.profession }}</div>
              <div class="sidebar-stats">
                <div class="stat-item">
                  <span class="stat-value">{{ total_followers }}</span>
                  <span class="stat-label">Following</span>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-item">
                  <span class="stat-value">{{ total_project }}</span>
                  <span class="stat-label">Projects</span>
                </div>
              </div>
              <router-link :to="'/sc/profile/' + user.slug" class="profile-link">
                View my profile
              </router-link>
            </div>
            <nav class="sidebar-nav">
              <router-link class="sidebar-link" to="/profile/update">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                Edit Profile
              </router-link>
              <router-link class="sidebar-link" to="/profile/change-password">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Change Password
              </router-link>
              <router-link class="sidebar-link" to="/notification-settings">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                Notification Settings
              </router-link>
              <router-link class="sidebar-link active" to="/my-Projects">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 20h20M4 20V10l8-6 8 6v10"/></svg>
                My Projects
              </router-link>
              <router-link class="sidebar-link" to="/my-followers">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                Following
              </router-link>
              <router-link class="sidebar-link" to="/my-blog-history">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                My Blog
              </router-link>
              <router-link class="sidebar-link" to="/my-service-requests">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                Service Requests
              </router-link>
            </nav>
          </div>
        </aside>

        <!-- Main content -->
        <main class="page-main">
          <div class="main-card">
            <div class="main-card-header">
              <div class="header-row">
                <div>
                  <h2 class="main-card-title">My Projects</h2>
                  <p class="main-card-subtitle">Projects you are connected to.</p>
                </div>
                <div class="header-actions">
                  <span class="count-badge">{{ projects.length }}</span>
                  <router-link to="/listings" class="btn-outline">
                    Browse All
                  </router-link>
                </div>
              </div>
            </div>

            <div class="main-card-body">
              <!-- Empty state -->
              <div v-if="projects.length === 0" class="empty-state">
                <div class="empty-icon">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M2 20h20M4 20V10l8-6 8 6v10"/></svg>
                </div>
                <p class="empty-title">No project connections yet</p>
                <p class="empty-sub">Browse and join construction projects in your area to get connected.</p>
                <router-link to="/listings" class="btn-primary">
                  Browse Projects
                </router-link>
              </div>

              <!-- Projects grid -->
              <div v-else class="projects-grid">
                <div
                  v-for="item in projects"
                  :key="item.id"
                  class="listing-card"
                >
                  <!-- Card image -->
                  <div class="project-image-wrap">
                    <img
                      v-if="item.project && item.project.images && item.project.images.length"
                      class="project-image"
                      :src="item.project.images[0].path || (imgUrl + 'projects/' + item.project.images[0].image)"
                      :alt="item.project.name"
                    />
                    <div v-else class="project-image-placeholder">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 20h20M4 20V10l8-6 8 6v10"/></svg>
                    </div>
                    <span class="project-badge">Project</span>
                  </div>

                  <!-- Card body -->
                  <div class="card-body">
                    <h3 class="project-title">
                      {{ item.project ? item.project.name : 'Untitled Project' }}
                    </h3>

                    <div class="project-meta" v-if="item.project">
                      <span v-if="item.project && item.project.status != null" class="meta-status" :class="item.project.status == 1 ? 'status-completed' : 'status-ongoing'">
                        {{ item.project.status == 1 ? 'Completed' : 'On-going' }}
                      </span>
                      <span v-if="item.project.city" class="meta-location">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {{ item.project.city.name || item.project.city }}<span v-if="item.project.state">, {{ item.project.state.name || item.project.state }}</span>
                      </span>
                    </div>

                    <router-link
                      v-if="item.project"
                      :to="'/project/' + item.project.id"
                      class="project-link"
                    >
                      View Project
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

      </div>
    </div>
  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout.vue';
import axios from 'axios';

export default {
  name: 'Projects',
  components: { AppLayout },

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      company: {},
      user: {},
      projects: [],
      total_project: 0,
      total_followers: 0,
    };
  },

  created() {
    this.fetchCompany();
    this.fetchUser();
    this.fetchProjects();
    this.fetchTotalProjects();
    this.fetchTotalFollowers();
  },

  methods: {
    logout() {
      localStorage.clear();
      this.$router.push({ name: 'Login' });
    },

    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res.status === 200) this.company = res.data; })
        .catch(() => {});
    },

    fetchUser() {
      axios(process.env.VUE_APP_API_URL + 'user/details/' + localStorage.getItem('user'))
        .then(res => { if (res.status === 200) this.user = res.data; })
        .catch(() => {});
    },

    fetchProjects() {
      axios(process.env.VUE_APP_API_URL + 'user/projects/' + localStorage.getItem('user'))
        .then(res => { if (res.status === 200) this.projects = res.data; })
        .catch(() => {});
    },

    fetchTotalProjects() {
      axios(process.env.VUE_APP_API_URL + 'project/total-project/' + localStorage.getItem('user'))
        .then(res => { if (res.status === 200) this.total_project = res.data; })
        .catch(() => {});
    },

    fetchTotalFollowers() {
      axios(process.env.VUE_APP_API_URL + 'user/total-following/' + localStorage.getItem('user'))
        .then(res => { if (res.status === 200) this.total_followers = res.data; })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.page-wrap {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  padding: 2.5rem 0;
}

/* ── Sidebar ── */
.page-sidebar {
  position: sticky;
  top: 80px;
  align-self: start;
}

.sidebar-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.sidebar-profile {
  padding: 1.5rem 1rem;
  text-align: center;
  border-bottom: 1px solid var(--border);
}

.sidebar-avatar {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  object-fit: cover;
  border: 3px solid var(--border);
}

.sidebar-name {
  margin-top: 0.75rem;
  font-weight: 700;
  color: var(--text);
  font-size: 0.95rem;
}

.sidebar-sub {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.sidebar-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-light);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
  line-height: 1.2;
}

.stat-label {
  font-size: 0.72rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stat-divider {
  width: 1px;
  height: 28px;
  background: var(--border);
}

.profile-link {
  display: inline-block;
  margin-top: 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
}

.profile-link:hover {
  text-decoration: underline;
}

.sidebar-nav {
  padding: 0.5rem 0;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.875rem;
  color: var(--text-muted);
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}

.sidebar-link:hover,
.sidebar-link.active {
  background: var(--surface-2);
  color: var(--primary);
}

.sidebar-link.active {
  font-weight: 600;
  border-right: 3px solid var(--accent);
}

.sidebar-link svg {
  flex-shrink: 0;
}

/* ── Main card ── */
.page-main {
  min-width: 0;
}

.main-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.main-card-header {
  padding: 1.25rem 1.75rem;
  border-bottom: 1px solid var(--border);
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.main-card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 0.2rem;
}

.main-card-subtitle {
  font-size: 0.82rem;
  color: var(--text-muted);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.6rem;
  background: var(--surface-3);
  color: var(--text);
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: var(--radius-full);
}

.main-card-body {
  padding: 1.75rem;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3.5rem 1rem;
  text-align: center;
}

.empty-icon {
  color: var(--border);
  margin-bottom: 1.25rem;
}

.empty-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 0.5rem;
}

.empty-sub {
  font-size: 0.85rem;
  color: var(--text-muted);
  max-width: 360px;
  line-height: 1.6;
  margin: 0 0 1.5rem;
}

/* ── Projects grid ── */
.projects-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
}

/* Extend global .listing-card */
.listing-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--surface);
  transition: box-shadow 0.15s, transform 0.15s;
}

.listing-card:hover {
  box-shadow: var(--shadow);
  transform: translateY(-2px);
}

.project-image-wrap {
  position: relative;
  height: 160px;
  background: var(--surface-3);
  overflow: hidden;
  flex-shrink: 0;
}

.project-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--border);
}

.project-badge {
  position: absolute;
  top: 0.6rem;
  left: 0.6rem;
  padding: 0.2rem 0.65rem;
  background: var(--primary);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: var(--radius-full);
  letter-spacing: 0.04em;
}

.card-body {
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.project-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.project-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.meta-status {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.15rem 0.55rem;
  border-radius: var(--radius-full);
  background: var(--surface-3);
  color: var(--text-muted);
}

.meta-status.status-active,
.meta-status.status-ongoing {
  background: #ecfdf5;
  color: var(--success);
}

.meta-status.status-completed {
  background: #eff6ff;
  color: #2563eb;
}

.meta-location {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.78rem;
  color: var(--text-muted);
}

.project-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: auto;
  padding-top: 0.5rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  border-top: 1px solid var(--border-light);
  transition: color 0.15s;
}

.project-link:hover {
  color: var(--accent);
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .page-wrap {
    grid-template-columns: 1fr;
    padding: 1.5rem 0;
  }

  .page-sidebar {
    position: static;
  }

  .sidebar-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.5rem;
  }

  .sidebar-link {
    padding: 0.5rem 0.75rem;
    border-radius: var(--radius);
    font-size: 0.8rem;
  }

  .sidebar-link.active {
    border-right: none;
    background: var(--surface-3);
  }

  .projects-grid {
    grid-template-columns: 1fr;
  }
}
</style>
